import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listProducts, fileUrl } from "../lib/api";
import { Search, Edit } from "lucide-react";
import { Input } from "../components/ui/input";
import { useIsAdmin } from "../lib/useIsAdmin";
import { useSeo } from "../lib/useSeo";
import { WhatsAppButton } from "../components/WhatsAppButton";

export default function Products() {
  useSeo({ title: "Solar Products", description: "Browse solar panels, inverters, batteries and complete rooftop kits from Arnav Enterprises, Akola. Compare prices and request a quote." });
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    listProducts().then(setProducts).catch(console.error);
  }, []);

  const categories = ["All", ...Array.from(new Set(products.map((p) => p.category)))];

  const filtered = products.filter((p) => {
    const matchCat = category === "All" || p.category === category;
    const matchQuery =
      !query ||
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      p.description.toLowerCase().includes(query.toLowerCase());
    return matchCat && matchQuery;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      <div className="mb-8 sm:mb-12">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3">Catalog</div>
        <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight mb-3 sm:mb-4">
          Everything solar, <br />in one place.
        </h1>
        <p className="text-muted-foreground max-w-xl">
          Browse our complete range of panels, inverters, batteries and accessories.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 sm:items-center justify-between mb-6 sm:mb-10">
        <div className="relative flex-1 min-w-0 sm:min-w-[240px] max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="product-search"
            placeholder="Search products..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-11 h-12 bg-white border-border rounded-full"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="category-filters">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                category === c
                  ? "bg-secondary text-white border-secondary"
                  : "bg-white text-secondary border-border hover:border-secondary"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground" data-testid="no-products">
          No products found.
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {filtered.map((p, i) => (
            <div
              key={p.id}
              onClick={() => navigate(`/products/${p.id}`)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/products/${p.id}`)}
              role="link"
              tabIndex={0}
              data-testid={`product-card-${p.id}`}
              className={`group relative bg-white border border-border rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer flex flex-col ${
                i % 5 === 0 ? "col-span-2 lg:col-span-2" : ""
              }`}
            >
              {isAdmin && (
                <Link
                  to={`/admin?edit=${p.id}`}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`catalog-edit-${p.id}`}
                  title="Edit product"
                  className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-md text-secondary border border-border rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm hover:bg-primary hover:text-white hover:border-primary transition-colors"
                >
                  <Edit className="w-3.5 h-3.5" strokeWidth={2} /> Edit
                </Link>
              )}
              <div className={`bg-muted overflow-hidden ${i % 5 === 0 ? "aspect-[16/9]" : "aspect-square sm:aspect-[4/3]"}`}>
                <img src={fileUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-3 sm:p-6 flex flex-col flex-1">
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-primary font-bold mb-1 sm:mb-2 truncate">
                  {p.category}
                </div>
                <div className="font-heading text-base sm:text-xl font-semibold leading-snug mb-1 sm:mb-2 line-clamp-2">
                  {p.name}
                </div>
                <div className="hidden sm:block text-sm text-muted-foreground line-clamp-2 mb-4">{p.description}</div>
                <div className="mt-auto flex flex-wrap items-center justify-between gap-2 sm:gap-3">
                  <div className="font-bold text-base sm:text-lg">₹{Number(p.price).toLocaleString("en-IN")}</div>
                  <div className="flex items-center gap-3">
                    <WhatsAppButton productName={p.name} compact testid={`wa-card-${p.id}`} />
                    <div className="hidden lg:block text-sm text-primary font-medium whitespace-nowrap">View details →</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
